import { Formik, Form, type FormikHelpers } from 'formik';
import { uniq, uniqBy } from 'lodash';
import { useState, useRef, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { Box } from '@hivebrite/design-system/Box';
import type { ModalElement } from '@hivebrite/design-system/Dialog';
import { useMatchBreakpoint } from '@hivebrite/design-system/Theme';

import FiltersBlock from 'hb-react/frontoffice/apps/Filters/components/FiltersBlock';
import { getInitialValues } from 'hb-react/frontoffice/apps/Filters/form/getInitialValues';
import { getSelectedFiltersCount } from 'hb-react/frontoffice/apps/Filters/form/getSelectedFiltersCount';
import type { UserTargetingProps } from 'hb-react/frontoffice/components/user_targeting/types';
import ModalWithTemplate from 'hb-react/shared/components/DesignSystem/Modal/ModalWithTemplate';
import { UjErrorBoundary } from 'hb-react/shared/components/ErrorBoundary';

import Filters from './components/Filters';
import { ModalFooter } from './components/ModalFooter';
import { type NotifyPeopleModalFooterProps } from './components/NotifyPeopleModalFooter';
import SelectedUsers from './components/SelectedUsers';
import Users from './components/Users';
import { MOBILE_STEPS } from './constants/mobileSteps';
import { useGetUsersFilters } from './hooks/useGetUsersFilters';
import {
  MAX_SELECTED_USERS_DISPLAYED,
  useInfiniteUsersTargeting,
} from './hooks/useInfiniteUsersTargeting';
import type { FormValues, NotifyUser } from './types';
import { flattenPages, markCheckboxesNotCheckedAsUndefined } from './utils';

interface NotifyPeopleModalProps {
  defaultQuery: UserTargetingProps['defaultQuery'];
  defaultSelectedUsers?: UserTargetingProps['defaultSelectedUsers'];
  maxTargetedUsers?: UserTargetingProps['maxTargetedUsers'];
  onCancelSelection?: UserTargetingProps['onCancelSelection'];
  onClose?: UserTargetingProps['onClose'];
  onValidateSelection?: UserTargetingProps['onValidateSelection'];
  setIsOpen: (isOpen: boolean) => void;
}

export const NotifyPeopleModal = ({
  // TODO: When removing flagHivehackNotifyPeople, we should update this prop name to baseQuery (as it's not a default value here)
  defaultQuery,
  defaultSelectedUsers,
  maxTargetedUsers = 250,
  // TODO: When removing flagHivehackNotifyPeople, we should update the props names so that they are cleaner and more consistent: onCancel, onConfirm, etc.
  onCancelSelection,
  onClose = () => {},
  onValidateSelection,
  setIsOpen,
}: NotifyPeopleModalProps) => {
  const modalRef = useRef<ModalElement>(null);

  const isMobile = useMatchBreakpoint(['s']);

  const [selectedUsers, setSelectedUsers] = useState<NotifyUser[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [detailedSearchOpened, setDetailedSearchOpened] = useState(false);
  const [selectedFiltersCount, setSelectedFiltersCount] = useState(0);
  const [mobileStep, setMobileStep] = useState(MOBILE_STEPS.USERS_LIST);
  const { topicId } = useParams<{
    topicId?: string;
  }>();
  const topicIdInt = topicId ? parseInt(topicId, 10) : undefined;

  const {
    data: filtersConfig,
    isPending: isLoadingFilters,
    isError: isFiltersError,
    refetch: refetchFilters,
  } = useGetUsersFilters(topicIdInt);
  const initialValues = useMemo(
    () =>
      getInitialValues({
        filters: filtersConfig,
        initialFiltersFromConfig: {
          who: '',
          address: '',
        },
      }),
    [filtersConfig],
  );
  const [query, setQuery] = useState<FormValues['query']>(
    markCheckboxesNotCheckedAsUndefined(initialValues.query),
  );

  const {
    data: filteredUsersData,
    fetchNextPage,
    isFetchingNextPage,
    isLoading: isLoadingFilteredUsers,
    isError: isFilteredUsersError,
  } = useInfiniteUsersTargeting({
    query: {
      ...defaultQuery,
      ...query,
    },
  });
  const flattenedUsers = useMemo(
    () => flattenPages(filteredUsersData),
    [filteredUsersData],
  );
  const hasMorePages = flattenedUsers.users.length < flattenedUsers.count;

  const filteredUsersIds = flattenedUsers.userIds || [];

  const preventSubmission =
    (!defaultSelectedUsers?.length && !selectedUserIds.length) ||
    selectedUserIds.length > maxTargetedUsers;

  // If adding all unselected filtered users would exceed the limit, disable Select All
  const alreadySelectedFilteredUsersCount = selectedUserIds.filter((id) =>
    filteredUsersIds.includes(id),
  ).length;
  const isSelectAllDisabled =
    selectedUserIds.length +
      (flattenedUsers.count - alreadySelectedFilteredUsersCount) >
    maxTargetedUsers;

  const handleSelectAllUsers = () => {
    // - newIds is the data we're using to keep track of the selected users
    // - newUsers is the data we're showing in the selected users list, it is used for display only
    // so we need to display either the max number set by the admin or the max number of users we can display (hard coded)
    let newIds = uniq([...selectedUserIds, ...filteredUsersIds]);
    const newUsers = uniqBy(
      [...selectedUsers, ...flattenedUsers.users],
      (user) => user.id,
    ).slice(0, Math.min(maxTargetedUsers, MAX_SELECTED_USERS_DISPLAYED));

    // We need to make sure we don't have more IDs than the maxTargetedUsers value
    // If so, we need to take only the first maxTargetedUsers users and use their ids to update the selectedUserIds
    // (to be sure both states have the same users selected)
    if (newIds.length > maxTargetedUsers) {
      newIds = newUsers.map((user) => user.id);
    }

    setSelectedUserIds(newIds);
    setSelectedUsers(newUsers);
  };

  const handleSelectUser = (user: NotifyUser) => {
    setSelectedUsers(uniqBy([...selectedUsers, user], 'id'));
    setSelectedUserIds(uniq([...selectedUserIds, user.id]));
  };

  const handleUnselectUser = (user) => {
    setSelectedUsers(
      selectedUsers.filter((selectedUser) => selectedUser.id !== user.id),
    );
    setSelectedUserIds(selectedUserIds.filter((id) => id !== user.id));
  };

  const handleClearCurrentFilteredSelection = () => {
    setSelectedUsers(
      selectedUsers.filter((user) => !flattenedUsers.userIds.includes(user.id)),
    );
    setSelectedUserIds(
      selectedUserIds.filter((id) => !flattenedUsers.userIds.includes(id)),
    );
  };

  const handleClearSelection = () => {
    setSelectedUsers([]);
    setSelectedUserIds([]);
  };

  const handleCancelFiltersClick = () => {
    setDetailedSearchOpened(false);
    setMobileStep(MOBILE_STEPS.USERS_LIST);

    setQuery(({ address, who }) => ({
      address,
      who,
    }));
  };

  const handleClearFilters = (
    setFieldValue: FormikHelpers<FormValues>['setFieldValue'],
  ) => {
    const newQuery = markCheckboxesNotCheckedAsUndefined(initialValues.query);

    setQuery(newQuery);
    setFieldValue('query', newQuery);
  };

  const handleConfirmSelection = () => {
    // Check that selectedUsers is what's expected
    onValidateSelection?.(selectedUsers, selectedUserIds);

    setIsOpen(false);
  };

  const handleSearchUsers = (values: FormValues) => {
    const newQuery = {
      ...query,
      ...values.query,
    };

    // In the query sent to the endpoint, checkboxes not checked should be ignored, not explicitly set to false
    const queryWithCheckboxesNotCheckedAsUndefined =
      markCheckboxesNotCheckedAsUndefined(newQuery);

    setQuery(queryWithCheckboxesNotCheckedAsUndefined);

    setDetailedSearchOpened(false);
    setMobileStep(MOBILE_STEPS.USERS_LIST);
  };

  const handleOnMoreFiltersClick = () => {
    setDetailedSearchOpened(true);
    setMobileStep(MOBILE_STEPS.MORE_FILTERS);
  };

  const handleCancelClick = () => {
    onCancelSelection?.();
    onClose?.();
  };

  useEffect(() => {
    if (defaultSelectedUsers) {
      setSelectedUsers(defaultSelectedUsers);
      setSelectedUserIds(defaultSelectedUsers.map((user) => user.id));
    }
  }, [defaultSelectedUsers]);

  // Get the selected filters count when the query changes
  useEffect(() => {
    if (Object.keys(query).length) {
      const filtersCount = getSelectedFiltersCount(
        filtersConfig,
        { query },
        false,
      );

      setSelectedFiltersCount(filtersCount);
    }
  }, [filtersConfig, query]);

  const header = useMemo(() => {
    if (isMobile && mobileStep === MOBILE_STEPS.SELECTED_USERS) {
      return s__('NotifyPeople|Review selection');
    }

    if (detailedSearchOpened) {
      return s__('NotifyPeople|More filters');
    }

    return s__('NotifyPeople|Notify people');
  }, [isMobile, mobileStep, detailedSearchOpened]);

  const getFooterProps = (): NotifyPeopleModalFooterProps => {
    if (isMobile) {
      switch (mobileStep) {
        case MOBILE_STEPS.SELECTED_USERS:
          return {
            customPrimaryButtonLabel: __('Confirm selection'),
            customSecondaryButtonLabel: __('Back'),
            isActionDisabled: !selectedUsers.length,
            onCancel: () => setMobileStep(MOBILE_STEPS.USERS_LIST),
            onConfirm: handleConfirmSelection,
          };

        case MOBILE_STEPS.USERS_LIST:
        default:
          return {
            customPrimaryButtonLabel: __('See selection'),
            isActionDisabled: preventSubmission,
            onCancel: handleCancelClick,
            onConfirm: () => setMobileStep(MOBILE_STEPS.SELECTED_USERS),
          };
      }
    }

    return {
      isActionDisabled: preventSubmission,
      onCancel: handleCancelClick,
      onConfirm: handleConfirmSelection,
    };
  };

  return (
    <Formik initialValues={initialValues} onSubmit={handleSearchUsers}>
      <Form>
        <ModalWithTemplate
          footer={
            <ModalFooter
              detailedSearchOpened={detailedSearchOpened}
              footerProps={getFooterProps()}
              onCancelFilters={handleCancelFiltersClick}
              onConfirmSearch={handleSearchUsers}
            />
          }
          footProps={
            isMobile
              ? {
                  // @ts-expect-error expected to be a BoxProps but footProps are spread so style is valid
                  style: {
                    boxShadow: '0px 1px 6px 0px rgba(0, 0, 0, 0.10)',
                  },
                }
              : {}
          }
          onCloseModal={onClose}
          isSticky
          isOpen
          pb={0}
          width="medium"
          header={header}
          headProps={
            detailedSearchOpened
              ? {
                  // @ts-expect-error expected to be a BoxProps but headProps are spread so style is valid
                  style: { boxShadow: '0px 1px 6px 0px rgba(0, 0, 0, 0.10)' },
                }
              : {}
          }
          style={{ overflow: 'hidden' }}
          screenReadersCloseButtonText={s__(
            'Accessibility|Close notify people window',
          )}
          shouldNotifyMobileApp={false}
          ref={modalRef}
        >
          <UjErrorBoundary
            context={{
              tags: {
                module: 'FO|NotifyPeopleContent',
              },
            }}
          >
            <Box
              height="100%"
              maxHeight={{ _: 'none', m: '60vh' }}
              flexDirection="column"
              width="100%"
            >
              {detailedSearchOpened && filtersConfig ? (
                <Box display="block" minWidth="100%" mt={6}>
                  <FiltersBlock
                    featureName="notify-people"
                    filters={filtersConfig?.overlay}
                    fullWidth
                    modalRef={modalRef}
                  />
                </Box>
              ) : (
                <>
                  {!isMobile || mobileStep === MOBILE_STEPS.USERS_LIST ? (
                    <>
                      <Filters
                        clearFilters={handleClearFilters}
                        filtersConfig={filtersConfig}
                        handleOnMoreFiltersClick={handleOnMoreFiltersClick}
                        hasError={isFiltersError}
                        isLoading={isLoadingFilters}
                        refetchFilters={refetchFilters}
                        selectedFiltersCount={selectedFiltersCount}
                      />
                      <Users
                        clearFilters={handleClearFilters}
                        clearSelection={handleClearCurrentFilteredSelection}
                        fetchNextPage={fetchNextPage}
                        users={flattenedUsers.users}
                        usersCount={flattenedUsers.count}
                        hasError={isFilteredUsersError}
                        hasMorePages={hasMorePages}
                        isFetchingNextPage={isFetchingNextPage}
                        isLoading={isLoadingFilteredUsers}
                        isMobile={isMobile}
                        maxTargetedUsers={maxTargetedUsers}
                        removeUser={handleUnselectUser}
                        selectAllUsers={handleSelectAllUsers}
                        selectUser={handleSelectUser}
                        selectedUserIds={selectedUserIds}
                        isSelectAllDisabled={isSelectAllDisabled}
                        modalRef={modalRef}
                      />
                    </>
                  ) : null}
                  {!isMobile || mobileStep === MOBILE_STEPS.SELECTED_USERS ? (
                    <SelectedUsers
                      clearSelection={handleClearSelection}
                      isMobile={isMobile}
                      maxTargetedUsers={maxTargetedUsers}
                      removeUser={handleUnselectUser}
                      selectedUsers={selectedUsers}
                      selectedUsersCount={selectedUserIds.length}
                    />
                  ) : null}
                </>
              )}
            </Box>
          </UjErrorBoundary>
        </ModalWithTemplate>
      </Form>
    </Formik>
  );
};
